import { LANGUAGE_NAMES, type Language } from '@lyrmo/shared/options';
import { createContext, useContext } from 'react';
import { ApiError } from '../api/api-error';
import {
  formatDate,
  formatDuration,
  formatFileSize,
  formatFullDate,
  formatNumber,
  formatPrice,
  formatResetTime,
  formatSavedAt,
} from '../lib/format';
import { en, type Messages } from './en';
import { directionOf, localeTag, type Locale } from './locales';

export interface I18n {
  readonly locale: Locale;
  readonly dir: 'ltr' | 'rtl';
  /** The interface's words in the current language. */
  readonly t: Messages;
  readonly format: ReturnType<typeof formatters>;
  /** What to tell the writer about a failed request. */
  readonly describeError: (error: unknown) => string;
  readonly setLocale: (locale: Locale) => void;
}

function formatters(locale: Locale, t: Messages) {
  const tag = localeTag(locale);
  const names = locale === 'en' ? null : new Intl.DisplayNames([tag], { type: 'language' });
  return {
    number: (value: number) => formatNumber(value, tag),
    duration: (seconds: number) => formatDuration(seconds, tag),
    fileSize: (bytes: number) => formatFileSize(bytes, tag),
    price: (amount: number, currency: string) => formatPrice(amount, currency, tag),
    date: (iso: string) => formatDate(iso, tag),
    fullDate: (iso: string) => formatFullDate(iso, tag),
    resetTime: (iso: string) => formatResetTime(iso, new Date(), tag, t.time.tomorrow),
    /** When something was saved: the time today, else the day. */
    savedAt: (iso: string) => formatSavedAt(iso, new Date(), tag),
    /** A language a rewrite can be written in, named in the interface's language. */
    language(language: Language): string {
      if (language === 'auto') return t.options.sameLanguage;
      const name = names?.of(language === 'zh' ? 'zh-Hans' : language);
      if (!name) return LANGUAGE_NAMES[language];
      // Some languages write language names in lowercase; a list reads better capitalized.
      return name.charAt(0).toLocaleUpperCase(tag) + name.slice(1);
    },
  };
}

function describer(locale: Locale, t: Messages, format: ReturnType<typeof formatters>) {
  return (error: unknown): string => {
    if (!(error instanceof ApiError)) return t.errors.generic;
    // The API explains problems in English, more fully than a code can.
    if (locale === 'en') return error.message;
    switch (error.code) {
      case 'NETWORK_ERROR':
        return t.errors.network;
      case 'RATE_LIMITED':
        return error.retryAfter === undefined
          ? t.errors.rateLimited
          : t.errors.rateLimitedFor(format.duration(error.retryAfter));
      case 'CREDIT_LIMIT_REACHED':
        return t.creditLimit.notEnough;
      case 'INVALID_SIGN_IN_CODE':
        return t.errors.invalidCode;
      case 'UNAUTHORIZED':
        return t.errors.signInRequired;
      case 'NO_TEXT_FOUND':
        return t.editor.feedback.unreadableFile;
      case 'BAD_REQUEST':
      case 'VALIDATION_FAILED':
      case 'UNSUPPORTED_MEDIA_TYPE':
        return t.errors.invalidRequest;
      case 'PAYLOAD_TOO_LARGE':
        return t.errors.tooLong;
      case 'REWRITE_REFUSED':
        return t.errors.refused;
      case 'ALREADY_SUBSCRIBED':
        return t.errors.alreadySubscribed;
      case 'NOT_SUBSCRIBED':
        return t.errors.notSubscribed;
      case 'SUBSCRIPTION_CHANGE_REFUSED':
        return t.errors.subscriptionChangeRefused;
      case 'NOT_FOUND':
        return t.errors.notFound;
      case 'TONE_NOT_FOUND':
        return t.errors.toneNotFound;
      case 'TONE_LIMIT_REACHED':
        return t.errors.toneLimit;
      case 'INSTRUCTION_NOT_FOUND':
        return t.errors.instructionNotFound;
      case 'INSTRUCTION_LIMIT_REACHED':
        return t.errors.instructionLimit;
      case 'TOOL_NOT_FOUND':
        return t.aiTools.notFound;
      case 'TOOL_LIMIT_REACHED':
        return t.aiTools.limitReached;
      case 'DRAFT_NOT_FOUND':
        return t.errors.draftNotFound;
      case 'DRAFT_LIMIT_REACHED':
        return t.errors.draftLimit;
      case 'UPSTREAM_ERROR':
      case 'UPSTREAM_TIMEOUT':
      case 'SERVICE_UNAVAILABLE':
      case 'INVALID_RESPONSE':
        return t.errors.unavailable;
      default:
        return t.errors.generic;
    }
  };
}

export function createI18n(locale: Locale, t: Messages, setLocale: (locale: Locale) => void): I18n {
  const format = formatters(locale, t);
  return {
    locale,
    dir: directionOf(locale),
    t,
    format,
    describeError: describer(locale, t, format),
    setLocale,
  };
}

// English until a provider says otherwise, so components also work on their own, as in tests.
export const I18nContext = createContext<I18n>(
  createI18n('en', en, () => {
    // No provider, no other language to switch to.
  }),
);

export const useI18n = (): I18n => useContext(I18nContext);
